import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { processGeocodingResult } from '@/utils/locationUtils';

const TYPED_COORDINATE_WAIT_MS = 400;

interface FormSetters {
  setLatitude: (value: string) => void;
  setLongitude: (value: string) => void;
  setAddress: (value: string) => void;
  setCity: (value: string) => void;
}

export async function reverseGeocode(lat: number, lng: number): Promise<{ address: string; city: string } | null> {
  try {
    if (typeof google === 'undefined' || !google.maps?.Geocoder) return null;
    const geocoder = new google.maps.Geocoder();
    const results = await new Promise<google.maps.GeocoderResult[]>((resolve, reject) => {
      geocoder.geocode({ location: { lat, lng } }, (res, status) => {
        if (status === 'OK' && res) resolve(res);
        else reject(new Error(`Reverse geocoding failed: ${status}`));
      });
    });
    if (results?.length) {
      const info = processGeocodingResult(results[0]);
      return { address: info.address || '', city: info.city || '' };
    }
  } catch (error) {
    console.error('Error reverse geocoding coordinates:', error);
  }
  return null;
}

// Only the latest lookup fills the address, so a slow reply cannot pair one place's address with another's pin
export function useStoryLocationSearch({ setLatitude, setLongitude, setAddress, setCity }: FormSetters) {
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [isGoogleMapsLoaded, setIsGoogleMapsLoaded] = useState(false);
  const [autocompleteService, setAutocompleteService] = useState<google.maps.places.AutocompleteService | null>(null);
  const [autocompleteResults, setAutocompleteResults] = useState<google.maps.places.AutocompletePrediction[]>([]);
  const [showAutocomplete, setShowAutocomplete] = useState(false);
  const lookup = useRef(0);
  const typedAddress = useRef(false);
  const typedTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    let attempts = 0;
    const checkGoogleMapsLoaded = () => {
      attempts++;
      if (typeof google !== 'undefined' && google.maps && google.maps.Geocoder && google.maps.places) {
        setIsGoogleMapsLoaded(true);
        setAutocompleteService(new google.maps.places.AutocompleteService());
      } else if (attempts < 50) {
        timeoutId = setTimeout(checkGoogleMapsLoaded, 100);
      } else {
        console.warn('Google Maps API failed to load after 5 seconds');
      }
    };
    checkGoogleMapsLoaded();
    return () => {
      clearTimeout(timeoutId);
      clearTimeout(typedTimer.current);
    };
  }, []);

  // A new lookup, or a location set some other way, supersedes any lookup still in flight
  const nextLookup = () => {
    clearTimeout(typedTimer.current);
    return ++lookup.current;
  };

  const fillPlace = (lat: number, lng: number, address: string, city?: string) => {
    nextLookup();
    typedAddress.current = false;
    setLatitude(lat.toString());
    setLongitude(lng.toString());
    setAddress(address);
    if (city !== undefined) setCity(city);
  };

  const onAddressChange = (value: string) => {
    typedAddress.current = true;
    setAddress(value);
  };

  const onCoordinateChange = (newLat: string, newLng: string) => {
    setLatitude(newLat);
    setLongitude(newLng);
    const n = nextLookup();
    const lat = parseFloat(newLat);
    const lng = parseFloat(newLng);
    if (!newLat || !newLng || isNaN(lat) || isNaN(lng)) return;
    typedTimer.current = setTimeout(async () => {
      const geo = await reverseGeocode(lat, lng);
      if (n !== lookup.current || !geo || typedAddress.current) return;
      setAddress(geo.address);
      setCity(geo.city);
    }, TYPED_COORDINATE_WAIT_MS);
  };

  // A pin dropped on the map moves the place, so its address follows even over a typed one
  const onMapPick = async (lat: number, lng: number) => {
    const n = nextLookup();
    setLatitude(lat.toString());
    setLongitude(lng.toString());
    const geo = await reverseGeocode(lat, lng);
    if (n !== lookup.current) return;
    if (geo) {
      typedAddress.current = false;
      setAddress(geo.address);
      setCity(geo.city);
      toast.success('Location and address updated!');
    } else {
      toast.success('Location updated!');
    }
  };

  const onSearchQueryChange = useCallback((query: string) => {
    setSearchQuery(query);
    if (!autocompleteService || !query.trim()) {
      setAutocompleteResults([]);
      setShowAutocomplete(false);
      return;
    }
    try {
      autocompleteService.getPlacePredictions({ input: query, types: ['geocode', 'establishment'] }, (predictions, status) => {
        if (status === google.maps.places.PlacesServiceStatus.OK && predictions) {
          setAutocompleteResults(predictions);
          setShowAutocomplete(true);
        } else {
          setAutocompleteResults([]);
          setShowAutocomplete(false);
        }
      });
    } catch (error) {
      console.error('Autocomplete error:', error);
      setAutocompleteResults([]);
      setShowAutocomplete(false);
    }
  }, [autocompleteService]);

  const onSearch = async () => {
    if (!searchQuery.trim()) return;
    if (!isGoogleMapsLoaded) {
      toast.error('Google Maps is still loading. Please try again in a moment.');
      return;
    }
    setIsSearching(true);
    const n = nextLookup();
    try {
      const geocoder = new google.maps.Geocoder();
      const results = await new Promise<google.maps.GeocoderResult[]>((resolve, reject) => {
        geocoder.geocode({ address: searchQuery }, (res, status) => {
          if (status === 'OK' && res) resolve(res);
          else reject(new Error(`Geocoding failed: ${status}`));
        });
      });
      if (n !== lookup.current) return;
      if (results && results.length > 0) {
        const location = results[0].geometry.location;
        fillPlace(location.lat(), location.lng(), results[0].formatted_address);
        setSearchQuery('');
        toast.success('Location found and updated!');
      } else {
        toast.error('No location found for this search query.');
      }
    } catch (error) {
      console.error('Error searching location:', error);
      toast.error('Failed to search location. Please try again.');
    } finally {
      setIsSearching(false);
    }
  };

  const onAutocompleteSelect = async (prediction: google.maps.places.AutocompletePrediction) => {
    setSearchQuery(prediction.description);
    setAutocompleteResults([]);
    setShowAutocomplete(false);
    if (!isGoogleMapsLoaded) {
      toast.error('Google Maps is still loading. Please try again.');
      return;
    }
    setIsSearching(true);
    const n = nextLookup();
    try {
      const geocoder = new google.maps.Geocoder();
      const results = await new Promise<google.maps.GeocoderResult[]>((resolve, reject) => {
        geocoder.geocode({ placeId: prediction.place_id }, (res, status) => {
          if (status === 'OK' && res) {
            resolve(res);
            return;
          }
          geocoder.geocode({ address: prediction.description }, (addressResults, addressStatus) => {
            if (addressStatus === 'OK' && addressResults) resolve(addressResults);
            else reject(new Error(`Both placeId (${status}) and address (${addressStatus}) geocoding failed`));
          });
        });
      });
      if (n !== lookup.current) return;
      if (results && results.length > 0) {
        const info = processGeocodingResult(results[0]);
        nextLookup();
        typedAddress.current = false;
        setLatitude(info.coordinates?.lat?.toString() ?? '');
        setLongitude(info.coordinates?.lng?.toString() ?? '');
        setAddress(info.address || '');
        setCity(info.city || '');
        toast.success('Location found and updated!');
      } else {
        toast.error('No location details found for this place.');
      }
    } catch (error) {
      console.error('Error geocoding selected place:', error);
      toast.error('Failed to get location details. Please try again.');
    } finally {
      setIsSearching(false);
    }
  };

  return {
    searchQuery,
    isSearching,
    isGoogleMapsLoaded,
    autocompleteResults,
    showAutocomplete,
    closeAutocomplete: () => setShowAutocomplete(false),
    onSearchQueryChange,
    onSearch,
    onAutocompleteSelect,
    onCoordinateChange,
    onMapPick,
    onAddressChange,
    fillPlace,
  };
}
